// VerReporteSupervisoras.js
// Panel tabular de solo lectura: reporte de visitas/inspecciones de SUPERVISORA
// (piso x día), con filtros, heatmap por pregunta, ranking de cumplimiento y
// evolución temporal. Misma visualización que VerReporteAsistentas.js, pero
// con preguntas a_qn='supervisora' (categoría de selección múltiple) y con la
// lógica de "ruta" (R1: lunes/jueves, R2: martes/viernes, R3: miércoles). Un
// reporte fuera del día obligatorio del piso se marca como "extra" (positivo).
// Accesible por: supervisora (sus pisos), admin_asistentas/admin_atcliente
// (solo supervisoras encargadas), admin/superadmin (todos).
// La configuración de filtros se guarda en localStorage por usuario y por PC.
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { ESTRELLAS_INFO, numEstrellas, textoEstrellas } from "../../utils/estrellas";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { todayISO, addDays, lunesISO, C, BG, KpiCard } from "../analisis/comun";
import { getServicioStyle } from "../../utils/servicioColores";
import { useCargaProgresiva, MasFilas } from "./cargaProgresiva";
import PanelFlotante from "../../components/PanelFlotante";
import { ToggleVista, EvolucionCumplimiento, MiniTendencia, CalendarioCumplimiento, EvolucionPreguntas, AtencionSinReportes, statsDias, DetalleSupervisora, EvolucionFranjas, SelectorEmpresas, EMPRESAS_POR_DEFECTO, EMPRESAS_REPORTE } from "./reporteEvolucion";

const RUTA_LABEL = { 1: "R1", 2: "R2", 3: "R3" };
const RUTA_COLOR = { 1: "#0ea5e9", 2: "#f59e0b", 3: "#8b5cf6" };

// Orden fijo para los mini-donuts de cumplimiento por servicio en la cabecera.
const ORDEN_SERVICIOS = ["PRIME", "TI", "PLUS", "G", "YP", "CTA"];

// Paleta cíclica para distinguir visualmente las columnas de cada categoría
// de preguntas en la tabla (se asigna por orden de aparición, no por nombre).
const CATEGORIA_PALETA = [
  { bg: "#eef2ff", text: "#4338ca", border: "#c7d2fe" },
  { bg: "#fef9c3", text: "#854d0e", border: "#fde68a" },
  { bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
  { bg: "#e0f2fe", text: "#0369a1", border: "#7dd3fc" },
  { bg: "#fae8ff", text: "#86198f", border: "#f0abfc" },
  { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
];

// ─────────────────────────────────────────────
// Popover de filtro tipo Excel: lista de valores únicos con checkboxes + buscador
// ─────────────────────────────────────────────
const ColumnFilterPopover = ({ opciones, seleccionados, onChange, renderLabel, renderOption }) => {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [temp, setTemp] = useState(seleccionados);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(seleccionados); }, [abierto, seleccionados]);

  const cerrar = useCallback(() => setAbierto(false), []);

  const etiqueta = (o) => (renderLabel ? renderLabel(o) : (o === "" ? "(vacío)" : String(o)));

  const filtradas = busqueda.trim()
    ? opciones.filter((o) => etiqueta(o).toLowerCase().includes(busqueda.trim().toLowerCase()))
    : opciones;

  const toggle = (v) => setTemp((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  const activo = seleccionados.length > 0;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: 5 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((a) => !a); }}
        title="Filtrar"
        style={{
          border: "none", background: activo ? "#4338ca" : "#e2e8f0",
          color: activo ? "#fff" : "#64748b", borderRadius: 5, cursor: "pointer",
          fontSize: 10, padding: "2px 6px", lineHeight: 1.4, fontWeight: 700,
        }}
      >▾</button>
      {abierto && (
        <PanelFlotante anchorRef={ref} onCerrar={cerrar} style={{
          background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 12, boxShadow: "0 16px 36px rgba(15,23,42,0.25)",
          width: 220, padding: 10, textAlign: "left", color: "#334155", textTransform: "none",
        }}>
          <input
            value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="🔍 Buscar…"
            style={{ width: "100%", padding: "6px 8px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12.5, marginBottom: 6, boxSizing: "border-box", outline: "none" }}
          />
          <div style={{ display: "flex", gap: 10, marginBottom: 6, fontSize: 11 }}>
            <span onClick={() => setTemp(filtradas)} style={{ color: "#4338ca", cursor: "pointer", fontWeight: 700 }}>Marcar todos</span>
            <span onClick={() => setTemp([])} style={{ color: "#94a3b8", cursor: "pointer", fontWeight: 700 }}>Desmarcar</span>
          </div>
          <div style={{ maxHeight: 190, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
            {filtradas.length === 0 && <div style={{ fontSize: 12, color: "#94a3b8", padding: 4 }}>Sin valores</div>}
            {filtradas.map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, cursor: "pointer", padding: "3px 2px" }}>
                <input type="checkbox" checked={temp.includes(o)} onChange={() => toggle(o)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{renderOption ? renderOption(o) : etiqueta(o)}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button onClick={() => { onChange([]); setAbierto(false); }} style={{ flex: 1, padding: "6px 0", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Limpiar</button>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={{ flex: 1, padding: "6px 0", borderRadius: 8, border: "none", background: "#4338ca", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Aplicar</button>
          </div>
        </PanelFlotante>
      )}
    </span>
  );
};

const TIEMPO_FILTRO_VACIO = { totalMin: "", totalMax: "", medioMin: "", medioMax: "", horaDesde: "", horaHasta: "", enHorario: "todos" };

// ─────────────────────────────────────────────
// Popover de filtro por rango de tiempo (total en minutos, medio por pregunta
// en segundos), por franja horaria de envío (hh:mm) y por si se envió dentro
// o fuera del horario de la asistenta asignada al piso ese día (no hay un
// horario propio de la supervisora, se usa el de la asistenta como
// referencia) — mismo aspecto que ColumnFilterPopover pero con inputs
// numéricos/hora en vez de una lista de valores.
// ─────────────────────────────────────────────
const RangoTiempoPopover = ({ filtro, onChange }) => {
  const [abierto, setAbierto] = useState(false);
  const [temp, setTemp] = useState(filtro);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(filtro); }, [abierto, filtro]);

  const cerrar = useCallback(() => setAbierto(false), []);

  const activo = Object.entries(filtro).some(([k, v]) => (k === "enHorario" ? v !== "todos" : v !== ""));
  const inputStyle = { width: "50%", padding: "10px 12px", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" };
  const etiquetaStyle = { fontSize: 13, fontWeight: 800, color: "#475569", marginBottom: 6 };

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: 5 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((a) => !a); }}
        title="Filtrar por tiempo"
        style={{
          border: "none", background: activo ? "#4338ca" : "#e2e8f0",
          color: activo ? "#fff" : "#64748b", borderRadius: 5, cursor: "pointer",
          fontSize: 10, padding: "2px 6px", lineHeight: 1.4, fontWeight: 700,
        }}
      >▾</button>
      {abierto && (
        <PanelFlotante anchorRef={ref} onCerrar={cerrar} style={{
          background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 14, boxShadow: "0 20px 44px rgba(15,23,42,0.3)",
          width: 300, padding: 16, textAlign: "left", color: "#334155", textTransform: "none",
        }}>
          <div style={etiquetaStyle}>⏱ Tiempo total (min)</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input type="number" min="0" placeholder="Mín" value={temp.totalMin} onChange={(e) => setTemp((t) => ({ ...t, totalMin: e.target.value }))} style={inputStyle} />
            <input type="number" min="0" placeholder="Máx" value={temp.totalMax} onChange={(e) => setTemp((t) => ({ ...t, totalMax: e.target.value }))} style={inputStyle} />
          </div>
          <div style={etiquetaStyle}>⏱ Tiempo medio por pregunta (seg)</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input type="number" min="0" placeholder="Mín" value={temp.medioMin} onChange={(e) => setTemp((t) => ({ ...t, medioMin: e.target.value }))} style={inputStyle} />
            <input type="number" min="0" placeholder="Máx" value={temp.medioMax} onChange={(e) => setTemp((t) => ({ ...t, medioMax: e.target.value }))} style={inputStyle} />
          </div>
          <div style={etiquetaStyle}>🕐 Hora de envío (hh:mm inicio–final)</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input type="time" value={temp.horaDesde} onChange={(e) => setTemp((t) => ({ ...t, horaDesde: e.target.value }))} style={inputStyle} />
            <input type="time" value={temp.horaHasta} onChange={(e) => setTemp((t) => ({ ...t, horaHasta: e.target.value }))} style={inputStyle} />
          </div>
          <div style={etiquetaStyle}>📍 Respecto al horario de la asistenta del piso</div>
          <select value={temp.enHorario} onChange={(e) => setTemp((t) => ({ ...t, enHorario: e.target.value }))}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: 15, boxSizing: "border-box", marginBottom: 14 }}>
            <option value="todos">Todos</option>
            <option value="dentro">✅ Dentro de horario</option>
                <option value="margen">🟠 Hasta 30 min tras la salida</option>
            <option value="fuera">🔴 Fuera de horario</option>
          </select>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button onClick={() => { onChange(TIEMPO_FILTRO_VACIO); setAbierto(false); }} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Limpiar</button>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "#4338ca", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Aplicar</button>
          </div>
        </PanelFlotante>
      )}
    </span>
  );
};

// ─────────────────────────────────────────────
// Selector de categorías: chip grande desplegable con checkboxes (selección MÚLTIPLE,
// a diferencia del reporte de asistentas que solo permite una).
// ─────────────────────────────────────────────
const CategoriaMultiSelector = ({ categorias, seleccionadas, onChange }) => {
  const [abierto, setAbierto] = useState(false);
  const [temp, setTemp] = useState(seleccionadas);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(seleccionadas); }, [abierto, seleccionadas]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("touchstart", handler); };
  }, []);

  const toggle = (c) => setTemp((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const label = categorias.length === 0
    ? "Sin categorías"
    : seleccionadas.length === 0
      ? "Ninguna categoría seleccionada"
      : seleccionadas.length === categorias.length
        ? `Todas las categorías (${categorias.length})`
        : `${seleccionadas.length} de ${categorias.length} categorías`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setAbierto((a) => !a)}
        disabled={categorias.length === 0}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          padding: "16px 20px", borderRadius: 18, border: "none",
          background: "linear-gradient(135deg,#6366f1,#4338ca)", color: "#fff",
          cursor: categorias.length ? "pointer" : "default",
          boxShadow: abierto ? "0 4px 14px rgba(67,56,202,0.45)" : "0 6px 20px rgba(79,70,229,0.32)",
          fontWeight: 800, fontSize: 17, letterSpacing: 0.2, transition: "box-shadow .15s",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>🗂️</span>
          {label}
        </span>
        <span style={{ fontSize: 13, opacity: 0.85, transform: abierto ? "rotate(180deg)" : "none", transition: "transform .18s" }}>▾</span>
      </button>
      {abierto && categorias.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, zIndex: 60,
          background: "#fff", borderRadius: 16, border: "1.5px solid #e0e7ff",
          boxShadow: "0 16px 40px rgba(30,27,75,0.22)", padding: 10,
          display: "flex", flexDirection: "column", gap: 3, maxHeight: 320, overflowY: "auto",
        }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 4, fontSize: 12.5, padding: "0 6px" }}>
            <span onClick={() => setTemp(categorias)} style={{ color: "#4338ca", cursor: "pointer", fontWeight: 700 }}>Marcar todas</span>
            <span onClick={() => setTemp([])} style={{ color: "#94a3b8", cursor: "pointer", fontWeight: 700 }}>Desmarcar</span>
          </div>
          {categorias.map((c) => (
            <label
              key={c}
              style={{
                padding: "10px 12px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15,
                display: "flex", alignItems: "center", gap: 10,
                background: temp.includes(c) ? "linear-gradient(135deg,#eef2ff,#e0e7ff)" : "transparent",
                color: temp.includes(c) ? "#4338ca" : "#334155",
              }}
            >
              <input type="checkbox" checked={temp.includes(c)} onChange={() => toggle(c)} />
              {c}
            </label>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 6, padding: "0 2px" }}>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: "#4338ca", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Aplicar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Render de respuestas (coherente con VerReporteDiario.js)
// ─────────────────────────────────────────────
const SEMAFORO_INFO = {
  verde: { label: "Bien", bg: "#16a34a", emoji: "🟢" },
  amarillo: { label: "Regular", bg: "#d97706", emoji: "🟡" },
  rojo: { label: "Mal", bg: "#dc2626", emoji: "🔴" },
  desconozco: { label: "?", bg: "#94a3b8", emoji: "❓" },
};
const SI_NO_INFO = {
  si: { label: "Sí", bg: "#16a34a", emoji: "✅" },
  no: { label: "No", bg: "#dc2626", emoji: "❌" },
};

const CeldaRespuesta = ({ pregunta, valor }) => {
  if (valor === undefined || valor === null || valor === "") {
    return <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>;
  }
  if (pregunta.tipo === "semaforo" || pregunta.tipo === "semaforo+desconozco") {
    const info = SEMAFORO_INFO[valor];
    if (!info) return <span style={{ fontSize: 12 }}>{valor}</span>;
    return <span style={{ ...st.badge, background: info.bg }}>{info.emoji} {info.label}</span>;
  }
  if (pregunta.tipo === "estrellas") {
    const n = numEstrellas(valor);
    if (n === null) return <span style={{ fontSize: 12 }}>{String(valor)}</span>;
    return <span title={`${n}/5 · ${ESTRELLAS_INFO[n].label}`} style={{ ...st.badge, background: ESTRELLAS_INFO[n].bg }}>★ {n}</span>;
  }
  if (pregunta.tipo === "si_no") {
    const info = SI_NO_INFO[valor];
    if (!info) return <span style={{ fontSize: 12 }}>{valor}</span>;
    return <span style={{ ...st.badge, background: info.bg }}>{info.emoji} {info.label}</span>;
  }
  if (pregunta.tipo === "opciones_color") {
    // respuesta múltiple: array de ids
    const ids = Array.isArray(valor) ? valor : [valor];
    return (
      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
        {ids.map((id) => {
          const op = (pregunta.valores || []).find((v) => String(v.id) === String(id));
          if (!op) return <span key={id} style={{ fontSize: 12 }}>{id}</span>;
          return <span key={id} style={{ ...st.badge, background: op.color || "#3b82f6" }}>{op.etiqueta}</span>;
        })}
      </span>
    );
  }
  return <span style={{ fontSize: 12, whiteSpace: "pre-wrap", maxWidth: 160, display: "inline-block" }} title={String(valor)}>{String(valor).slice(0, 40)}{String(valor).length > 40 ? "…" : ""}</span>;
};

// Preguntas de la categoría "Residentes" se responden una vez POR CADA alumno del
// piso (suele haber ~4). En vez de un único valor, se muestra un recuento
// compacto por cada valor posible (p.ej. 🟢2 🟡1 ❓1), fácil de leer de un vistazo.
// Al pasar el ratón (hover) o pulsar (móvil) sobre un badge, se ven los nombres
// de los alumnos detrás de ese recuento.
const CeldaResidentes = ({ pregunta, resumen, total }) => {
  const [abierto, setAbierto] = useState(null); // valor cuyo popover de nombres está abierto

  // resumen[valor] es ahora una lista de nombres (antes era solo el recuento)
  const entradas = Object.entries(resumen || {}).filter(([, nombres]) => (nombres || []).length > 0);
  if (entradas.length === 0) {
    return <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>;
  }
  const infoDe = (valor) => {
    if (pregunta.tipo === "semaforo" || pregunta.tipo === "semaforo+desconozco") return SEMAFORO_INFO[valor];
    if (pregunta.tipo === "si_no") return SI_NO_INFO[valor];
    if (pregunta.tipo === "estrellas") {
      const n = numEstrellas(valor);
      return n === null ? null : { emoji: `${n}★`, bg: ESTRELLAS_INFO[n].bg };
    }
    if (pregunta.tipo === "opciones_color") {
      const op = (pregunta.valores || []).find((v) => String(v.id) === String(valor));
      return op ? { emoji: "", bg: op.color || "#3b82f6" } : null;
    }
    return null;
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center", alignItems: "center" }} title={total ? `${total} alumno${total !== 1 ? "s" : ""}` : undefined}>
      {entradas.map(([valor, nombres]) => {
        const info = infoDe(valor);
        const isOpen = abierto === valor;
        return (
          <span
            key={valor}
            onClick={(e) => { e.stopPropagation(); setAbierto(isOpen ? null : valor); }}
            title={nombres.join(", ")}
            style={{ ...st.badge, background: info?.bg || "#94a3b8", padding: "0 6px", fontSize: 11, cursor: "pointer", position: "relative" }}
          >
            {info?.emoji || valor} {nombres.length}
            {isOpen && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
                  marginTop: 4, background: "#1e293b", color: "#fff", padding: "6px 10px",
                  borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)", zIndex: 50, textAlign: "left",
                }}
              >
                {nombres.map((n, i) => <div key={i}>{n}</div>)}
              </div>
            )}
          </span>
        );
      })}
    </div>
  );
};

const etiquetaValorPregunta = (pregunta, valor) => {
  if (valor === "") return "— Sin respuesta —";
  if (pregunta.tipo === "semaforo" || pregunta.tipo === "semaforo+desconozco") {
    const info = SEMAFORO_INFO[valor];
    return info ? `${info.emoji} ${info.label}` : String(valor);
  }
  if (pregunta.tipo === "estrellas") {
    const n = numEstrellas(valor);
    return n === null ? String(valor) : `${textoEstrellas(n)} ${n}/5`;
  }
  if (pregunta.tipo === "si_no") {
    const info = SI_NO_INFO[valor];
    return info ? `${info.emoji} ${info.label}` : String(valor);
  }
  if (pregunta.tipo === "opciones_color") {
    const op = (pregunta.valores || []).find((v) => String(v.id) === String(valor));
    return op ? op.etiqueta : String(valor);
  }
  return String(valor);
};

// extraJuntoNumero: muestra los extras debajo del número (a la derecha) en vez
// de debajo de la barra (lo usa "Cumplimiento por día").
const BarraCumplimiento = ({ label, enviados, total, pct, extra, extraJuntoNumero = false }) => {
  const color = pct == null ? "#94a3b8" : pct >= 80 ? C.verde : pct >= 50 ? C.amarillo : C.rojo;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: 12.5, fontWeight: 700, color: "#374151", marginBottom: 3 }}>
        <span style={{ textTransform: "capitalize" }}>{label}</span>
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.25 }}>
          <span style={{ color, fontWeight: 800 }}>{enviados}/{total} · {pct != null ? `${pct}%` : "—"}</span>
          {extraJuntoNumero && !!extra && (
            <span style={{ fontSize: 11, fontWeight: 800, color: "#7c3aed" }}>✨ +{extra} extra</span>
          )}
        </span>
      </div>
      <div style={{ display: "flex", height: 10, borderRadius: 6, overflow: "hidden", background: "#f3f4f6" }}>
        <div style={{ width: `${pct || 0}%`, background: color, transition: "width .3s" }} />
      </div>
      {!extraJuntoNumero && !!extra && (
        <div style={{ marginTop: 3, fontSize: 11, fontWeight: 800, color: "#7c3aed" }}>✨ +{extra} extra</div>
      )}
    </div>
  );
};

const PieChart = ({ segmentos, size = 84 }) => {
  const total = segmentos.reduce((s, x) => s + x.valor, 0);
  if (!total) return null;
  let acc = 0;
  const stops = segmentos.filter((s) => s.valor > 0).map((s) => {
    const desde = (acc / total) * 100;
    acc += s.valor;
    const hasta = (acc / total) * 100;
    return `${s.color} ${desde}% ${hasta}%`;
  }).join(", ");
  return <div style={{ width: size, height: size, borderRadius: "50%", background: `conic-gradient(${stops})`, flexShrink: 0, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }} />;
};

// Anillo de cumplimiento con los números (enviados/total) en el centro, en vez de
// un porcentaje. Vive debajo de los filtros de período; mientras carga muestra
// un anillo neutro con "…".
const DonutNumeros = ({ enviados, total, size = 70, loading, compacto = false }) => {
  const pct = total ? Math.round((enviados / total) * 100) : null;
  const color = loading ? "#94a3b8" : pct == null ? "#94a3b8" : pct >= 80 ? C.verde : pct >= 50 ? C.amarillo : C.rojo;
  const r = (size - 9) / 2, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = !loading && pct != null ? Math.max(0, Math.min(1, pct / 100)) * circ : 0;
  // hueco útil dentro del anillo y tamaño de letra que cabe según las cifras
  const hueco = 2 * r - (compacto ? 5 : 8) - 8;
  const ajustar = (texto, base) => Math.max(7, Math.min(base, hueco / (String(texto).length * 0.6)));
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={compacto ? 5 : 8} />
      {dash > 0 && (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={compacto ? 5 : 8}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: "stroke-dasharray 0.6s" }} />
      )}
      {loading ? (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={Math.max(9, size * 0.24)} fontWeight={800} fill="#94a3b8">…</text>
      ) : compacto && `${enviados}/${total}`.length <= 5 ? (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={ajustar(`${enviados}/${total}`, size * 0.24)} fontWeight={800} fill="#1e293b">
          {enviados}/{total}
        </text>
      ) : (
        // dos líneas (enviados arriba, /total abajo) y letra que se encoge con
        // el número de cifras para no salirse nunca del anillo
        <>
          <text x={cx} y={cy - size * 0.08} textAnchor="middle" dominantBaseline="middle" fontSize={ajustar(String(enviados), size * (compacto ? 0.24 : 0.27))} fontWeight={800} fill="#1e293b">
            {enviados}
          </text>
          <text x={cx} y={cy + size * 0.17} textAnchor="middle" dominantBaseline="middle" fontSize={ajustar(`/${total}`, size * (compacto ? 0.16 : 0.15))} fontWeight={700} fill="#94a3b8">
            /{total}
          </text>
        </>
      )}
    </svg>
  );
};

// Insignia de cobertura extra (pisos visitados fuera de su día obligatorio): se
// muestra siempre FUERA del anillo de cumplimiento, nunca como un trozo más —
// no cuenta para ese total, así que integrarla en el propio donut sería engañoso.
const BadgeExtra = ({ n, compacto = false }) => {
  if (!n) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
      fontSize: compacto ? 11.5 : 13.5, fontWeight: 800, color: "#fff",
      background: "linear-gradient(135deg,#a855f7,#7c3aed)",
      padding: compacto ? "3px 10px" : "5px 14px", borderRadius: 999,
      boxShadow: "0 3px 10px rgba(124,58,237,0.4)",
    }}>
      ✨ +{n} extra
    </span>
  );
};

const HeatmapPregunta = ({ pregunta, filas }) => {
  const id = String(pregunta.id);
  let segmentos = [];
  let total = 0;

  if (pregunta.tipo === "semaforo" || pregunta.tipo === "semaforo+desconozco") {
    const c = contarPregunta(filas, id);
    segmentos = [
      { label: "Bien", valor: c.verde, color: C.verde },
      { label: "Regular", valor: c.amarillo, color: C.amarillo },
      { label: "Mal", valor: c.rojo, color: C.rojo },
      { label: "Desconoce", valor: c.desconozco, color: "#94a3b8" },
    ];
    total = c.total;
  } else if (pregunta.tipo === "estrellas") {
    const conteos = [0, 0, 0, 0, 0, 0];
    filas.forEach((f) => {
      if (!f.enviado) return;
      const resumenAlumnos = f.respuestas_alumnos?.[id];
      if (resumenAlumnos) {
        Object.entries(resumenAlumnos).forEach(([v, nombres]) => { const n = numEstrellas(v); if (n !== null) { conteos[n] += (nombres || []).length; total += (nombres || []).length; } });
      } else {
        const n = numEstrellas(f.respuestas?.[id]);
        if (n !== null) { conteos[n]++; total++; }
      }
    });
    segmentos = [1, 2, 3, 4, 5].map((n) => ({ label: `${n} ★`, valor: conteos[n], color: ESTRELLAS_INFO[n].bg }));
  } else if (pregunta.tipo === "si_no") {
    const c = contarPregunta(filas, id);
    segmentos = [{ label: "Sí", valor: c.verde, color: C.verde }, { label: "No", valor: c.rojo, color: C.rojo }];
    total = c.total;
  } else if (pregunta.tipo === "opciones_color") {
    const conteos = new Map();
    filas.forEach((f) => {
      if (!f.enviado) return;
      const resumenAlumnos = f.respuestas_alumnos?.[id];
      if (resumenAlumnos) {
        // cada valor es ahora una lista de nombres de alumnos, no un recuento directo
        Object.entries(resumenAlumnos).forEach(([v, nombres]) => {
          const n = (nombres || []).length;
          conteos.set(String(v), (conteos.get(String(v)) || 0) + n);
          total += n;
        });
      } else {
        const v = f.respuestas?.[id];
        if (v === undefined || v === null || v === "") return;
        // respuesta múltiple: cuenta cada opción marcada
        (Array.isArray(v) ? v : [v]).forEach((x) => { conteos.set(String(x), (conteos.get(String(x)) || 0) + 1); total++; });
      }
    });
    segmentos = (pregunta.valores || []).map((op) => ({ label: op.etiqueta, valor: conteos.get(String(op.id)) || 0, color: op.color || "#3b82f6" }));
  } else {
    let respondidas = 0;
    filas.forEach((f) => {
      if (!f.enviado) return;
      const resumenAlumnos = f.respuestas_alumnos?.[id];
      if (resumenAlumnos) {
        // cada valor es ahora una lista de nombres de alumnos, no un recuento directo
        const n = Object.values(resumenAlumnos).reduce((a, nombres) => a + (nombres || []).length, 0);
        total += n;
        respondidas += n;
      } else {
        total++;
        const v = f.respuestas?.[id];
        if (v !== undefined && v !== null && v !== "") respondidas++;
      }
    });
    segmentos = [{ label: "Respondida", valor: respondidas, color: C.azul }, { label: "Sin responder", valor: total - respondidas, color: "#e2e8f0" }];
  }

  if (!total) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #f1f5f9" }}>
      <PieChart segmentos={segmentos} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 4 }}>{pregunta.emoji} {pregunta.subcategoria}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {segmentos.filter((s) => s.valor > 0).map((s) => (
            <span key={s.label} style={{ fontSize: 11.5, color: "#475569", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: s.color, display: "inline-block" }} />
              {s.label} {s.valor} ({Math.round((s.valor / total) * 100)}%)
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Períodos rápidos
// ─────────────────────────────────────────────
const PERIODOS_DIA = [
  { key: "hoy", label: "Hoy" },
  { key: "ayer", label: "Ayer" },
  { key: "semana", label: "Esta semana" },
  { key: "sem_ant", label: "Sem. anterior" },
  { key: "custom", label: "Personalizado" },
];
function rangoDePeriodo(key, custom) {
  const hoy = todayISO(), lun = lunesISO();
  if (key === "hoy") return { desde: hoy, hasta: hoy };
  if (key === "ayer") { const a = addDays(hoy, -1); return { desde: a, hasta: a }; }
  if (key === "semana") return { desde: lun, hasta: hoy };
  if (key === "sem_ant") return { desde: addDays(lun, -7), hasta: addDays(lun, -1) };
  return custom;
}

function contarPregunta(filas, idPregunta) {
  const c = { verde: 0, amarillo: 0, rojo: 0, desconozco: 0, total: 0 };
  const sumar = (v, n) => {
    if (v === undefined || v === null || v === "") return;
    c.total += n;
    if (v === "si") c.verde += n;
    else if (v === "no") c.rojo += n;
    else if (v === "verde" || v === "amarillo" || v === "rojo" || v === "desconozco") c[v] += n;
  };
  filas.forEach((f) => {
    if (!f.enviado) return;
    // Preguntas de "Residentes": una respuesta por cada alumno del piso (n_estudiantes
    // respuestas por formulario), en vez de 1 sola respuesta por formulario.
    const resumenAlumnos = f.respuestas_alumnos?.[idPregunta];
    if (resumenAlumnos) {
      // cada valor es ahora una lista de nombres de alumnos, no un recuento directo
      Object.entries(resumenAlumnos).forEach(([v, nombres]) => sumar(v, (nombres || []).length));
    } else {
      sumar(f.respuestas?.[idPregunta], 1);
    }
  });
  return c;
}

const formatHora = (isoString) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (isNaN(d)) return null;
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
};

const formatDuracion = (segundos) => {
  if (segundos == null) return null;
  const total = Math.round(segundos);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// Insignia estilo "reloj digital": dígitos monoespaciados en una pastilla
// clara (sin fondo oscuro). "variante" permite pintarla en verde/rojo (p.ej.
// para marcar si el reporte se envió el día que tocaba o no).
const RELOJ_ESTILOS = {
  neutro: { background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0" },
  verde: { background: "#dcfce7", color: "#15803d", border: "1px solid #86efac" },
  rojo: { background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5" },
  naranja: { background: "#ffedd5", color: "#c2410c", border: "1px solid #fdba74" },
};

// ── Respeto de horarios ──────────────────────────────────────────────────
// Tres franjas según la hora de envío respecto al horario de la asistenta:
//   en horario (entre entrada y salida), hasta 30 min tras la salida, y fuera
//   de horario (antes de la entrada o más de 30 min después de la salida).
const MARGEN_SALIDA_MIN = 30;
const aMinutos = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const franjaHorario = (f) => {
  if (!f.enviado || !f.fecha_hora) return null;
  const envio = aMinutos(String(f.fecha_hora).slice(11, 16));
  const entrada = aMinutos(f.hora_entrada), salida = aMinutos(f.hora_salida);
  if (envio == null || entrada == null || salida == null) return null;
  if (envio < entrada) return "fuera";
  if (envio <= salida) return "dentro";
  return envio - salida <= MARGEN_SALIDA_MIN ? "margen" : "fuera";
};
const FRANJAS_HORARIO = [
  { k: "dentro", label: "En horario", icono: "✅", color: "#16a34a" },
  { k: "margen", label: `Hasta ${MARGEN_SALIDA_MIN} min tras la salida`, icono: "🟠", color: "#ea580c" },
  { k: "fuera", label: "Fuera de horario", icono: "🔴", color: "#dc2626" },
];
const contarFranjas = (filas) => {
  const c = { dentro: 0, margen: 0, fuera: 0 };
  filas.forEach((f) => { const fr = franjaHorario(f); if (fr) c[fr]++; });
  return { ...c, total: c.dentro + c.margen + c.fuera };
};

// Barra 100 % apilada con las tres franjas (con número y % en cada trozo)
const BarraFranjas = ({ c, alto = 22, etiquetas = true }) => {
  if (!c.total) return <div style={{ fontSize: 12, color: "#94a3b8" }}>Sin reportes con horario para comparar.</div>;
  return (
    <div style={{ display: "flex", gap: 2, height: alto, borderRadius: 6, overflow: "hidden" }}>
      {FRANJAS_HORARIO.map((fr) => {
        const n = c[fr.k]; if (!n) return null;
        const pct = Math.round((n / c.total) * 100);
        return (
          <div key={fr.k} title={`${fr.label}: ${n} (${pct}%)`} style={{
            flex: n, background: fr.color, color: "#fff", fontSize: 11.5, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center", minWidth: 4, whiteSpace: "nowrap", overflow: "hidden",
          }}>{etiquetas && pct >= 9 ? `${pct}%` : ""}</div>
        );
      })}
    </div>
  );
};

const AGRUPACIONES_RESPETO = [{ clave: "supervisora", texto: "supervisora", valor: (f) => f.user_supervisora }];

// Botón desplegable (píldora) a juego con los selectores índigo del reporte
const BotonDesplegable = ({ abierto, onClick, texto, n }) => (
  <button onClick={onClick} style={{
    display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 999,
    border: "2px solid #c7d2fe", background: abierto ? "#eef2ff" : "linear-gradient(135deg,#fff,#eef2ff)",
    color: "#3730a3", fontWeight: 800, fontSize: 12.5, cursor: "pointer",
    boxShadow: abierto ? "none" : "0 2px 8px rgba(79,70,229,0.15)", transition: "all .15s",
  }}>
    <span style={{ display: "inline-block", transition: "transform .2s", transform: abierto ? "rotate(180deg)" : "none" }}>▾</span>
    {texto}
    {n != null && <span style={{ background: "#4338ca", color: "#fff", borderRadius: 999, padding: "1px 8px", fontSize: 11 }}>{n}</span>}
  </button>
);

// agrupaciones: [{ clave, texto, valor: (fila) => nombre }] — un desplegable por cada una
const RespetoHorarios = ({ filas, agrupaciones }) => {
  const [abierta, setAbierta] = React.useState(null);
  const [itemAbierto, setItemAbierto] = React.useState(null);
  const [verEvolucion, setVerEvolucion] = React.useState(false);
  const [filtroSup, setFiltroSup] = React.useState("todas");
  const c = React.useMemo(() => contarFranjas(filas), [filas]);
  const agrupar = (ag, fuente) => {
    const m = new Map();
    fuente.forEach((f) => { const k = ag.valor(f) || "—"; if (!m.has(k)) m.set(k, []); m.get(k).push(f); });
    return Array.from(m.entries()).map(([nombre, fs]) => ({ nombre, fs, c: contarFranjas(fs) }))
      .filter((x) => x.c.total > 0)
      .sort((a, b) => (b.c.fuera / b.c.total) - (a.c.fuera / a.c.total) || (b.c.margen / b.c.total) - (a.c.margen / a.c.total));
  };
  const grupos = React.useMemo(() => agrupaciones.map((ag) => ({ ...ag, items: agrupar(ag, filas) })), [filas, agrupaciones]); // eslint-disable-line react-hooks/exhaustive-deps
  const supervisorasResp = React.useMemo(() => [...new Set(filas.filter((f) => franjaHorario(f)).map((f) => f.user_supervisora || "—"))].sort(), [filas]);
  const variosDias = React.useMemo(() => new Set(filas.filter((f) => franjaHorario(f)).map((f) => f.fecha)).size > 1, [filas]);
  const visibles = grupos.filter((g) => g.items.length > 1);
  const grupoAbierto = visibles.find((g) => g.clave === abierta);
  const conFiltroSup = grupoAbierto?.filtroSupervisora && supervisorasResp.length > 1;
  const actual = grupoAbierto && conFiltroSup && filtroSup !== "todas"
    ? { ...grupoAbierto, items: agrupar(grupoAbierto, filas.filter((f) => (f.user_supervisora || "—") === filtroSup)) }
    : grupoAbierto;
  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 800, color: "#1e293b" }}>⏰ Respeto de horarios</h3>
      <BarraFranjas c={c} alto={26} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 10 }}>
        {FRANJAS_HORARIO.map((fr) => (
          <div key={fr.k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#334155" }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: fr.color }} />
            <span>{fr.icono} {fr.label}</span>
            <strong>{c[fr.k]}</strong>
            <span style={{ color: "#94a3b8" }}>({c.total ? Math.round((c[fr.k] / c.total) * 100) : 0}%)</span>
          </div>
        ))}
      </div>
      {(visibles.length > 0 || variosDias) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
          {variosDias && (
            <BotonDesplegable abierto={verEvolucion} texto="📈 Ver evolución" onClick={() => setVerEvolucion((v) => !v)} />
          )}
          {visibles.map((g) => (
            <BotonDesplegable key={g.clave} abierto={abierta === g.clave} n={g.items.length}
              texto={`Ver por ${g.texto}`} onClick={() => { setItemAbierto(null); setAbierta((a) => (a === g.clave ? null : g.clave)); }} />
          ))}
        </div>
      )}
      {verEvolucion && variosDias && (
        <div style={{ marginTop: 12, padding: "12px 14px", background: "#f8fafc", border: "1px solid #e0e7ff", borderRadius: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#475569", marginBottom: 8 }}>📈 Evolución del respeto de horarios <span style={{ fontWeight: 500, color: "#94a3b8" }}>(pasa el ratón por cada columna)</span></div>
          <EvolucionFranjas filas={filas} franja={franjaHorario} franjas={FRANJAS_HORARIO} alto={220} />
        </div>
      )}
      {actual && conFiltroSup && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>👩‍💼 Supervisora:</span>
          <select value={filtroSup} onChange={(e) => { setFiltroSup(e.target.value); setItemAbierto(null); }} style={{
            padding: "7px 12px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 12.5,
            background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3",
            cursor: "pointer", boxShadow: "0 2px 8px rgba(79,70,229,0.15)", outline: "none",
          }}>
            <option value="todas">Todas las supervisoras</option>
            {supervisorasResp.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
      {actual && actual.items.length === 0 && (
        <div style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 10 }}>Sin reportes con horario para esta supervisora.</div>
      )}
      {actual && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12, maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
          {actual.items.map(({ nombre, fs, c: cs }) => {
            const abiertoItem = itemAbierto === nombre;
            const subs = abiertoItem && actual.sub ? (() => {
              const m = new Map();
              fs.forEach((f) => { const k = actual.sub.valor(f) || "—"; if (!m.has(k)) m.set(k, []); m.get(k).push(f); });
              return Array.from(m.entries()).map(([n, sfs]) => ({ nombre: n, c: contarFranjas(sfs) })).filter((x) => x.c.total > 0)
                .sort((a, b) => (b.c.fuera / b.c.total) - (a.c.fuera / a.c.total) || (b.c.margen / b.c.total) - (a.c.margen / a.c.total));
            })() : [];
            return (
              <div key={nombre}>
                <div onClick={() => setItemAbierto((x) => (x === nombre ? null : nombre))} title="Pulsa para ver su evolución"
                  style={{ display: "grid", gridTemplateColumns: "minmax(90px, 170px) 1fr minmax(120px, auto)", gap: 10, alignItems: "center", cursor: "pointer", padding: "3px 4px", borderRadius: 8, background: abiertoItem ? "#eef2ff" : "transparent" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={nombre}>
                    <span style={{ display: "inline-block", color: "#6366f1", marginRight: 4, transition: "transform .2s", transform: abiertoItem ? "rotate(90deg)" : "none" }}>▸</span>{nombre}
                  </div>
                  <BarraFranjas c={cs} alto={16} />
                  <div style={{ fontSize: 11.5, color: "#64748b", whiteSpace: "nowrap" }}>✅ {cs.dentro} · 🟠 {cs.margen} · 🔴 {cs.fuera}</div>
                </div>
                {abiertoItem && (
                  <div style={{ margin: "6px 0 8px", padding: "10px 12px", background: "#f8fafc", border: "1px solid #e0e7ff", borderRadius: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 6 }}>📈 Evolución</div>
                    <EvolucionFranjas filas={fs} franja={franjaHorario} franjas={FRANJAS_HORARIO} alto={150} />
                    {subs.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>👤 Por {actual.sub.texto}</div>
                        {subs.map((x) => (
                          <div key={x.nombre} style={{ display: "grid", gridTemplateColumns: "minmax(90px, 160px) 1fr minmax(120px, auto)", gap: 10, alignItems: "center" }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={x.nombre}>{x.nombre}</div>
                            <BarraFranjas c={x.c} alto={14} />
                            <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>✅ {x.c.dentro} · 🟠 {x.c.margen} · 🔴 {x.c.fuera}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: "#94a3b8" }}>Ordenado de más a menos envíos fuera de horario. Pulsa en una fila para ver su evolución.</div>
        </div>
      )}
    </div>
  );
};
const RelojDigital = ({ icono, children, title, variante = "neutro" }) => (
  <div title={title} style={{
    display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "center",
    fontFamily: "'Courier New', Consolas, monospace",
    fontWeight: 800, fontSize: 13, padding: "4px 9px", borderRadius: 7,
    letterSpacing: 0.6, whiteSpace: "nowrap",
    ...(RELOJ_ESTILOS[variante] || RELOJ_ESTILOS.neutro),
  }}>
    {icono && <span style={{ fontSize: 11 }}>{icono}</span>}
    {children}
  </div>
);

// Celda con la hora en que se envió el reporte, el tiempo total empleado y el
// tiempo medio por pregunta (calculados en backend a partir de
// "tiempo_segundos" de cada respuesta), cada uno en su propio "reloj digital"
// apilado verticalmente. La hora se pinta en verde si se envió el mismo día
// que le tocaba (f.fecha) y dentro del horario de la asistenta del piso ese
// día (f.hora_entrada/f.hora_salida, reconstruido históricamente en
// backend), o en rojo si se envió otro día o fuera de ese horario.
const CeldaHoraTiempo = ({ f }) => {
  if (!f.enviado) return <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>;
  const hora = formatHora(f.fecha_hora);
  const fechaEnvio = f.fecha_hora ? String(f.fecha_hora).slice(0, 10) : null;
  const diaCorrecto = fechaEnvio && f.fecha ? fechaEnvio === f.fecha : null;
  const franja = franjaHorario(f);

  let variante = "neutro";
  let titulo = "Hora de envío";
  if (diaCorrecto === false) {
    variante = "rojo";
    titulo = "Enviado en un día distinto al que correspondía";
  } else if (franja === "fuera") {
    variante = "rojo";
    titulo = `Enviado fuera del horario de la asistenta (antes de la entrada o más de ${MARGEN_SALIDA_MIN} min después de la salida)`;
  } else if (franja === "margen") {
    variante = "naranja";
    titulo = `Enviado hasta ${MARGEN_SALIDA_MIN} min después de la hora de salida`;
  } else if (diaCorrecto === true) {
    variante = "verde";
    titulo = franja === "dentro" ? "Enviado el día y dentro del horario que correspondía" : "Enviado el día que correspondía";
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      {hora && (
        <RelojDigital icono="🕒" variante={variante} title={titulo}>
          {hora}
        </RelojDigital>
      )}
      {f.tiempo_total_segundos != null && (
        <RelojDigital icono="⏱" title="Tiempo total / tiempo medio por pregunta">
          {formatDuracion(f.tiempo_total_segundos)}
          {f.tiempo_medio_segundos != null && (
            <span style={{ color: "#64748b", fontWeight: 700 }}>· {f.tiempo_medio_segundos}s</span>
          )}
        </RelojDigital>
      )}
    </div>
  );
};

// Interruptor de las gráficas de cumplimiento: sumar los extras al %
const BotonSumarExtras = ({ activo, onClick }) => (
  <button type="button" onClick={onClick} aria-pressed={activo}
    title="Si está activado, los reportes extra cuentan como hechos y como visitas en el %"
    style={{
      border: activo ? "2px solid #7c3aed" : "1px solid #cbd5e1", background: activo ? "#7c3aed" : "#fff",
      color: activo ? "#fff" : "#5b21b6", borderRadius: 999, padding: "5px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer",
    }}>
    {activo ? "✓ Extras sumados" : "➕ Añadir extras"}
  </button>
);
const NotaExtrasSumados = () => (
  <div style={{ fontSize: 12, color: "#5b21b6", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "5px 10px", marginBottom: 8 }}>
    ✨ Extras sumados: % = (reportes hechos + extras) / (visitas obligatorias + extras).
  </div>
);

const VerReporteSupervisoras = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const rol = user?.tipo || "";
  const esSupervisora = rol === "supervisora";
  const usuario = (user && (user.user || user)) || "anon";

  // Configuración de filtros guardada en localStorage, por usuario y por PC.
  const STORAGE_KEY = `filtros_reporte_supervisoras_${usuario}`;
  const [guardado] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  const [periodo, setPeriodo] = useState(guardado.periodo || "hoy");
  const [custom, setCustom] = useState(guardado.custom || { desde: todayISO(), hasta: todayISO() });
  const { desde, hasta } = rangoDePeriodo(periodo, custom);

  const [filtroEstado, setFiltroEstado] = useState(guardado.filtroEstado || "todos"); // todos | respondidos | sin_responder | extra
  // Combinado con spread (no reemplazo directo) para que una configuración ya
  // guardada en localStorage de ANTES de añadir "preguntas"/"tiempo" no se
  // quede sin esas claves (y rompa el resto del código al leerlas).
  const [colFiltros, setColFiltros] = useState({
    piso: [], supervisora: [], ruta: [], preguntas: {}, tiempo: TIEMPO_FILTRO_VACIO,
    ...(guardado.colFiltros || {}),
  });
  const actualizarColFiltro = (campo, valores) => setColFiltros((c) => ({ ...c, [campo]: valores }));
  const actualizarFiltroPregunta = (idPregunta, valores) =>
    setColFiltros((c) => ({ ...c, preguntas: { ...c.preguntas, [idPregunta]: valores } }));

  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState(
    guardado.categoriasSeleccionadas !== undefined ? guardado.categoriasSeleccionadas : null
  ); // null = todavía no inicializado (se rellenará con todas al cargar preguntas)

  // Persistir configuración en localStorage (por PC) cada vez que cambie.
  useEffect(() => {
    if (categoriasSeleccionadas === null) return; // aún no cargado, no sobrescribir
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        periodo, custom, filtroEstado, colFiltros, categoriasSeleccionadas,
      }));
    } catch { /* localStorage no disponible: se ignora */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [STORAGE_KEY, periodo, custom, filtroEstado, colFiltros, categoriasSeleccionadas]);

  const hayFiltrosActivos = useMemo(
    () => filtroEstado !== "todos" || colFiltros.piso.length > 0 || colFiltros.supervisora.length > 0 || colFiltros.ruta.length > 0
      || Object.values(colFiltros.preguntas || {}).some((v) => v.length > 0)
      || Object.entries(colFiltros.tiempo || {}).some(([k, v]) => (k === "enHorario" ? v !== "todos" : v !== "")),
    [filtroEstado, colFiltros]
  );
  const limpiarFiltros = () => {
    setFiltroEstado("todos");
    setColFiltros({ piso: [], supervisora: [], ruta: [], preguntas: {}, tiempo: TIEMPO_FILTRO_VACIO });
  };

  const multiDia = desde !== hasta;

  const [todasLasPreguntas, setTodasLasPreguntas] = useState([]);
  const [filasTodas, setFilas] = useState([]);
  // Empresa (CH / CH-SS / CM): filtra TODO (tabla y resumen). Por defecto solo CH; la
  // supervisora ("Mis reportes") ve por defecto todas.
  const [empresas, setEmpresas] = useState(() => (esSupervisora ? [...EMPRESAS_REPORTE] : EMPRESAS_POR_DEFECTO));
  const filas = useMemo(() => filasTodas.filter((f) => f.empresa == null || empresas.includes(f.empresa)), [filasTodas, empresas]);
  const conteoEmpresas = useMemo(() => {
    const m = {};
    const vistos = new Set();
    filasTodas.forEach((f) => { if (f.empresa && !vistos.has(f.id_piso)) { vistos.add(f.id_piso); m[f.empresa] = (m[f.empresa] || 0) + 1; } });
    return m;
  }, [filasTodas]);
  const [opciones, setOpciones] = useState({ supervisoras: [], pisos: [], rutas: [], supervisoras_inactivas: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showResumen, setShowResumen] = useState(false);
  const [donutsAbiertos, setDonutsAbiertos] = useState(true);
  const [vistaDia, setVistaDia] = useState("linea"); // "linea" | "barras"
  const [sumarExtras, setSumarExtras] = useState(false); // gráficas: extras cuentan en el % (hechos y total)
  const [supAbierta, setSupAbierta] = useState(null); // supervisora desplegada en "Cumplimiento por supervisora"
  const [filtroSupervisoraHeatmap, setFiltroSupervisoraHeatmap] = useState("todas");

  // Preguntas activas que responde la propia supervisora (a_qn='supervisora').
  useEffect(() => {
    axios.get("/api/servicio/reporte_opciones?a_qn=supervisora&solo_activas=1")
      .then((r) => {
        const lista = Array.isArray(r.data) ? r.data : [];
        setTodasLasPreguntas(lista);
        const vistas = new Set();
        const cats = [];
        lista.forEach((p) => { if (!vistas.has(p.categoria)) { vistas.add(p.categoria); cats.push(p.categoria); } });
        // Si venía una selección guardada de una sesión anterior, se "sanea" contra las
        // categorías que existen AHORA: si alguna se ha desactivado/eliminado desde
        // entonces, se descarta en vez de seguir contando como seleccionada (evitaba
        // que el contador mostrara p.ej. "8 de 5" con categorías que ya no existen).
        setCategoriasSeleccionadas((prev) => (prev === null ? cats : prev.filter((c) => cats.includes(c))));
      })
      .catch(() => setTodasLasPreguntas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoriasDisponibles = useMemo(() => {
    const vistas = new Set();
    const lista = [];
    todasLasPreguntas.forEach((p) => { if (!vistas.has(p.categoria)) { vistas.add(p.categoria); lista.push(p.categoria); } });
    return lista;
  }, [todasLasPreguntas]);

  const preguntas = useMemo(
    () => todasLasPreguntas.filter((p) => (categoriasSeleccionadas || []).includes(p.categoria)),
    [todasLasPreguntas, categoriasSeleccionadas]
  );

  // Agrupa las preguntas visibles por categoría (contiguas, en el orden de
  // aparición) para poder pintarlas con un tono de color distinto por
  // categoría y mostrar el nombre de la categoría sobre sus columnas.
  const preguntasAgrupadas = useMemo(() => {
    const porCategoria = new Map();
    preguntas.forEach((p) => {
      if (!porCategoria.has(p.categoria)) porCategoria.set(p.categoria, []);
      porCategoria.get(p.categoria).push(p);
    });
    return Array.from(porCategoria.entries()).map(([categoria, lista], i) => ({
      categoria,
      preguntas: lista,
      color: CATEGORIA_PALETA[i % CATEGORIA_PALETA.length],
    }));
  }, [preguntas]);
  const preguntasOrdenadas = useMemo(() => preguntasAgrupadas.flatMap((g) => g.preguntas), [preguntasAgrupadas]);
  const colorDePregunta = useMemo(() => {
    const mapa = new Map();
    preguntasAgrupadas.forEach((g) => g.preguntas.forEach((p) => mapa.set(p.id, g.color)));
    return mapa;
  }, [preguntasAgrupadas]);

  const opcionesPiso = useMemo(
    () => (opciones.pisos || []).map((p) => p.abreviatura).filter(Boolean).sort(),
    [opciones.pisos]
  );
  const opcionesSupervisora = useMemo(() => (opciones.supervisoras || []).slice().sort(), [opciones.supervisoras]);

  useEffect(() => {
    if (filtroSupervisoraHeatmap !== "todas" && !opcionesSupervisora.includes(filtroSupervisoraHeatmap)) {
      setFiltroSupervisoraHeatmap("todas");
    }
  }, [opcionesSupervisora, filtroSupervisoraHeatmap]);

  // Filas del heatmap, filtradas por supervisora (independiente de los demás
  // filtros de la tabla principal).
  const filasHeatmap = useMemo(() => {
    if (filtroSupervisoraHeatmap === "todas") return filas;
    return filas.filter((f) => f.user_supervisora === filtroSupervisoraHeatmap);
  }, [filas, filtroSupervisoraHeatmap]);
  const supervisorasInactivasSet = useMemo(() => new Set(opciones.supervisoras_inactivas || []), [opciones.supervisoras_inactivas]);
  const renderOpcionSupervisora = useCallback((s) => (
    <>
      <span style={supervisorasInactivasSet.has(s) ? { color: "#94a3b8" } : undefined}>{s}</span>
      {supervisorasInactivasSet.has(s) && <span style={st.badgeBajaFiltro}>BAJA</span>}
    </>
  ), [supervisorasInactivasSet]);
  const opcionesRuta = useMemo(() => (opciones.rutas || []).slice().sort((a, b) => a - b), [opciones.rutas]);

  // Valores únicos presentes para una pregunta (para su filtro tipo Excel); incluye "" si hay filas sin responder
  // Las preguntas de "Residentes" (una respuesta por alumno) no guardan su
  // valor en f.respuestas sino en f.respuestas_alumnos[idPregunta] = { valor:
  // [nombres...] } — hay que mirar ahí también o su filtro sale siempre vacío.
  const opcionesPregunta = useCallback((p) => {
    const set = new Set();
    let hayVacios = false;
    const idStr = String(p.id);
    filas.forEach((f) => {
      const bucket = f.respuestas_alumnos?.[idStr];
      if (bucket && typeof bucket === "object") {
        const claves = Object.keys(bucket);
        if (claves.length === 0) hayVacios = true;
        claves.forEach((v) => set.add(v));
        return;
      }
      const v = f.respuestas?.[idStr];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) { hayVacios = true; return; }
      if (Array.isArray(v)) v.forEach((x) => set.add(String(x))); // respuesta múltiple
      else set.add(v);
    });
    const arr = Array.from(set);
    if (hayVacios) arr.push("");
    return arr;
  }, [filas]);

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    params.set("fecha_inicio", desde);
    params.set("fecha_fin", hasta);
    axios.get(`/api/reporte-supervisoras/panel?${params.toString()}`)
      .then((r) => {
        setFilas(r.data.filas || []);
        setOpciones(r.data.opciones || { supervisoras: [], pisos: [], rutas: [], supervisoras_inactivas: [] });
      })
      .catch(() => setError("❌ No se pudo cargar el reporte."))
      .finally(() => setLoading(false));
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const aplicarColFiltros = useCallback((arr) => arr.filter((f) => {
    if (colFiltros.piso.length && !colFiltros.piso.includes(f.abreviatura)) return false;
    if (colFiltros.supervisora.length && !colFiltros.supervisora.includes(f.user_supervisora)) return false;
    if (colFiltros.ruta.length && !colFiltros.ruta.includes(f.ruta)) return false;
    for (const [idPregunta, seleccionados] of Object.entries(colFiltros.preguntas || {})) {
      if (!seleccionados.length) continue;
      const bucket = f.respuestas_alumnos?.[idPregunta];
      if (bucket && typeof bucket === "object") {
        const claves = Object.keys(bucket);
        const coincide = claves.some((v) => seleccionados.includes(v)) || (claves.length === 0 && seleccionados.includes(""));
        if (!coincide) return false;
        continue;
      }
      const v = f.respuestas?.[idPregunta];
      if (Array.isArray(v)) { // respuesta múltiple: vale si alguna de las marcadas está seleccionada
        if (!v.some((x) => seleccionados.includes(String(x)))) return false;
        continue;
      }
      const valorNormalizado = (v === undefined || v === null) ? "" : v;
      if (!seleccionados.includes(valorNormalizado)) return false;
    }
    const t = colFiltros.tiempo || TIEMPO_FILTRO_VACIO;
    if (t.totalMin !== "" && (f.tiempo_total_segundos == null || f.tiempo_total_segundos < Number(t.totalMin) * 60)) return false;
    if (t.totalMax !== "" && (f.tiempo_total_segundos == null || f.tiempo_total_segundos > Number(t.totalMax) * 60)) return false;
    if (t.medioMin !== "" && (f.tiempo_medio_segundos == null || f.tiempo_medio_segundos < Number(t.medioMin))) return false;
    if (t.medioMax !== "" && (f.tiempo_medio_segundos == null || f.tiempo_medio_segundos > Number(t.medioMax))) return false;
    if (t.horaDesde !== "" || t.horaHasta !== "") {
      const horaEnvio = f.fecha_hora ? String(f.fecha_hora).slice(11, 16) : null;
      if (!horaEnvio) return false;
      if (t.horaDesde !== "" && horaEnvio < t.horaDesde) return false;
      if (t.horaHasta !== "" && horaEnvio > t.horaHasta) return false;
    }
    if (t.enHorario !== "todos") {
      const fr = franjaHorario(f);
      if (!fr || fr !== t.enHorario) return false;
    }
    return true;
  }), [colFiltros]);

  const tieneRespuestaCategoria = useCallback((f) => preguntas.some((p) => {
    const v = f.respuestas?.[String(p.id)];
    return v !== undefined && v !== null && v !== "";
  }), [preguntas]);

  const filasFiltradas = useMemo(() => aplicarColFiltros(filas), [filas, aplicarColFiltros]);

  const filasVisibles = useMemo(() => {
    if (filtroEstado === "respondidos") return filasFiltradas.filter((f) => f.enviado && tieneRespuestaCategoria(f));
    if (filtroEstado === "sin_responder") return filasFiltradas.filter((f) => !f.enviado || !tieneRespuestaCategoria(f));
    if (filtroEstado === "extra") return filasFiltradas.filter((f) => f.es_extra);
    return filasFiltradas;
  }, [filasFiltradas, filtroEstado, tieneRespuestaCategoria]);

  const gruposPorPiso = useMemo(() => {
    if (!multiDia) return [];
    const orden = [];
    const vistos = new Set();
    filasVisibles.forEach((f) => { if (!vistos.has(f.id_piso)) { vistos.add(f.id_piso); orden.push(f.id_piso); } });
    const porPiso = new Map();
    filasVisibles.forEach((f) => {
      if (!porPiso.has(f.id_piso)) porPiso.set(f.id_piso, []);
      porPiso.get(f.id_piso).push(f);
    });
    return orden.map((idPiso) => {
      const dias = porPiso.get(idPiso).slice().sort((a, b) => a.fecha.localeCompare(b.fecha));
      return { piso: dias[0], dias };
    });
  }, [filasVisibles, multiDia]);
  const carga = useCargaProgresiva(filasVisibles, gruposPorPiso);

  // Solo las filas "mandatorias" (día obligatorio según la ruta del piso) cuentan
  // para el cumplimiento; las "extra" (es_extra=true) son cobertura de bonus y no
  // penalizan ni suman al total esperado.
  const filasMandatorias = useMemo(() => filas.filter((f) => !f.es_extra), [filas]);
  const filasExtra = useMemo(() => filas.filter((f) => f.es_extra), [filas]);

  const statsSupervisoras = useMemo(() => {
    const mapa = new Map();
    filasMandatorias.forEach((f) => {
      const key = f.user_supervisora || "—";
      if (!mapa.has(key)) mapa.set(key, { nombre: key, enviados: 0, total: 0, extra: 0 });
      const e = mapa.get(key);
      e.total++;
      if (f.enviado) e.enviados++;
    });
    filasExtra.forEach((f) => {
      const key = f.user_supervisora || "—";
      if (!mapa.has(key)) mapa.set(key, { nombre: key, enviados: 0, total: 0, extra: 0 });
      mapa.get(key).extra++;
    });
    return Array.from(mapa.values())
      .map((e) => ({ ...e, pct: e.total ? Math.round((e.enviados / e.total) * 100) : null }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [filasMandatorias, filasExtra]);

  // Serie diaria de cada supervisora para la mini-tendencia
  const seriesSupervisoras = useMemo(() => {
    const m = new Map();
    filasMandatorias.forEach((f) => {
      const key = f.user_supervisora || "—";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(f);
    });
    const ex = new Map();
    filasExtra.forEach((f) => { const key = f.user_supervisora || "—"; if (!ex.has(key)) ex.set(key, []); ex.get(key).push(f); });
    return new Map(Array.from(m.entries()).map(([k, fs]) => [k, statsDias(fs, ex.get(k) || [])]));
  }, [filasMandatorias, filasExtra]);

  const statsPorDia = useMemo(() => {
    const mapa = new Map();
    const nuevo = (fecha) => ({ fecha, enviados: 0, total: 0, extra: 0 });
    filasMandatorias.forEach((f) => {
      if (!mapa.has(f.fecha)) mapa.set(f.fecha, nuevo(f.fecha));
      const e = mapa.get(f.fecha);
      e.total++;
      if (f.enviado) e.enviados++;
    });
    // extras (cobertura fuera de la ruta del día) de cada día
    filasExtra.forEach((f) => {
      if (!mapa.has(f.fecha)) mapa.set(f.fecha, nuevo(f.fecha));
      mapa.get(f.fecha).extra++;
    });
    return Array.from(mapa.values())
      .map((e) => ({ ...e, pct: e.total ? Math.round((e.enviados / e.total) * 100) : null }))
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [filasMandatorias, filasExtra]);

  // Para "Evolución" y "Calendario": con "Añadir extras" los extras cuentan como
  // reportes hechos y como visitas (enviados/total), y ya no se muestran aparte.
  const statsPorDiaGraf = useMemo(() => {
    if (!sumarExtras) return statsPorDia;
    const envExtra = new Map();
    filasExtra.forEach((f) => { if (f.enviado) envExtra.set(f.fecha, (envExtra.get(f.fecha) || 0) + 1); });
    return statsPorDia.map((d) => {
      const enviados = d.enviados + (envExtra.get(d.fecha) || 0), total = d.total + d.extra;
      return { ...d, enviados, total, extra: 0, pct: total ? Math.round((enviados / total) * 100) : null };
    });
  }, [statsPorDia, filasExtra, sumarExtras]);

  const statsPorRuta = useMemo(() => {
    const mapa = new Map();
    filasMandatorias.forEach((f) => {
      const key = f.ruta ?? "—";
      if (!mapa.has(key)) mapa.set(key, { nombre: RUTA_LABEL[f.ruta] || `Ruta ${key}`, enviados: 0, total: 0, extra: 0 });
      const e = mapa.get(key);
      e.total++;
      if (f.enviado) e.enviados++;
    });
    filasExtra.forEach((f) => {
      const key = f.ruta ?? "—";
      if (!mapa.has(key)) mapa.set(key, { nombre: RUTA_LABEL[f.ruta] || `Ruta ${key}`, enviados: 0, total: 0, extra: 0 });
      mapa.get(key).extra++;
    });
    return Array.from(mapa.values())
      .map((e) => ({ ...e, pct: e.total ? Math.round((e.enviados / e.total) * 100) : null }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [filasMandatorias, filasExtra]);

  const totalesGlobales = useMemo(() => {
    const total = filasMandatorias.length;
    const enviados = filasMandatorias.filter((f) => f.enviado).length;
    return { total, enviados, sinEnviar: total - enviados, pct: total ? Math.round((enviados / total) * 100) : null };
  }, [filasMandatorias]);

  // Mini-donuts de cumplimiento por servicio (cabecera), en el orden fijo de
  // ORDEN_SERVICIOS; los servicios sin ningún piso en el ámbito actual no se muestran.
  const totalesPorServicio = useMemo(() => {
    const mapa = new Map();
    filasMandatorias.forEach((f) => {
      const key = f.servicio;
      if (!key) return;
      if (!mapa.has(key)) mapa.set(key, { total: 0, enviados: 0 });
      const e = mapa.get(key);
      e.total++;
      if (f.enviado) e.enviados++;
    });
    // Cobertura extra por servicio (no forma parte de total/enviados de arriba,
    // se cuenta aparte para mostrarla junto a cada mini-donut).
    const mapaExtra = new Map();
    filasExtra.forEach((f) => {
      if (!f.servicio) return;
      mapaExtra.set(f.servicio, (mapaExtra.get(f.servicio) || 0) + 1);
    });
    return ORDEN_SERVICIOS
      .map((servicio) => ({ servicio, ...(mapa.get(servicio) || { total: 0, enviados: 0 }), extra: mapaExtra.get(servicio) || 0 }))
      .filter((e) => e.total > 0 || e.extra > 0);
  }, [filasMandatorias, filasExtra]);

  const nSinReporte = filasVisibles.filter((f) => !f.enviado).length;

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8" }}>
      <div style={{ background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "14px 16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "8px 14px" }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Reporte de visitas</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>👩‍💼 Reporte de Supervisoras</div>
        </div>
      </div>

      {/* Selector de categorías (múltiple) */}
      <div style={{ padding: "16px 16px 12px", background: "linear-gradient(180deg,#fff,#f8fafc)", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 11.5, fontWeight: 800, color: "#6366f1", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6 }}>
          Categorías del reporte de supervisora
        </div>
        <CategoriaMultiSelector categorias={categoriasDisponibles} seleccionadas={categoriasSeleccionadas || []} onChange={setCategoriasSeleccionadas} />
      </div>

      {/* Filtros de período */}
      <div style={{ display: "flex", gap: 6, padding: "10px 14px", overflowX: "auto", background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        {PERIODOS_DIA.map((p) => (
          <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
            padding: "7px 14px", borderRadius: 20,
            border: periodo === p.key ? "1.5px solid #1e293b" : "1.5px solid #e2e8f0",
            background: periodo === p.key ? "#1e293b" : "#f8fafc",
            color: periodo === p.key ? "#fff" : "#475569",
            fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
          }}>
            {p.label}
          </button>
        ))}
      </div>
      {periodo === "custom" && (
        <div style={{ display: "flex", gap: 10, padding: "8px 16px", alignItems: "center", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Desde</label>
          <input type="date" value={custom.desde} max={custom.hasta}
            onChange={(e) => setCustom((c) => ({ ...c, desde: e.target.value }))}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
          <label style={{ fontSize: 13, fontWeight: 600 }}>Hasta</label>
          <input type="date" value={custom.hasta} min={custom.desde} max={todayISO()}
            onChange={(e) => setCustom((c) => ({ ...c, hasta: e.target.value }))}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
        </div>
      )}

      {/* Cumplimiento (quesitos): debajo de los filtros de período, se cierra al cambiar de período */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        <button
          onClick={() => setDonutsAbiertos((v) => !v)}
          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "none", border: "none", cursor: "pointer" }}
        >
          <span style={{ fontSize: 12.5, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5 }}>📊 Cumplimiento</span>
          <span style={{ fontSize: 13, color: "#94a3b8", transform: donutsAbiertos ? "rotate(180deg)" : "none", transition: "transform .2s" }}>▾</span>
        </button>
        {donutsAbiertos && (
          <div style={{ padding: "6px 20px 26px", display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <DonutNumeros enviados={totalesGlobales.enviados} total={totalesGlobales.total} loading={loading} size={104} />
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 700 }}>han hecho el formulario</span>
              {!loading && <BadgeExtra n={filasExtra.length} />}
            </div>
            {!loading && totalesPorServicio.length > 0 && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 20, flexWrap: "wrap", paddingLeft: 20, borderLeft: "1px solid #e2e8f0" }}>
                {totalesPorServicio.map((e) => (
                  <div key={e.servicio} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                    <DonutNumeros enviados={e.enviados} total={e.total} size={66} compacto />
                    <span style={{ fontSize: 11.5, fontWeight: 800, color: "#475569", letterSpacing: 0.3 }}>{e.servicio}</span>
                    <BadgeExtra n={e.extra} compacto />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filtros de selección */}
      <div style={{ display: "flex", gap: 10, padding: "12px 14px", flexWrap: "wrap", background: "#fff", borderBottom: "1px solid #e2e8f0", alignItems: "center" }}>
        <SelectorEmpresas seleccion={empresas} onChange={setEmpresas} conteo={conteoEmpresas} />
        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
          style={{
            padding: "12px 18px", borderRadius: 14, border: "2px solid #c7d2fe", fontSize: 15,
            background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3",
            cursor: "pointer", boxShadow: "0 3px 10px rgba(79,70,229,0.15)", outline: "none",
          }}>
          <option value="todos">📋 Todos</option>
          <option value="respondidos">✅ Solo respondidos</option>
          <option value="sin_responder">⬜ Solo sin responder</option>
          <option value="extra">✨ Solo cobertura extra</option>
        </select>
        {hayFiltrosActivos && (
          <button onClick={limpiarFiltros} style={{
            padding: "9px 14px", borderRadius: 10, border: "1.5px solid #dc2626",
            background: "#fee2e2", color: "#991b1b", fontWeight: 700, fontSize: 12.5, cursor: "pointer",
          }}>
            ✕ Quitar todos los filtros
          </button>
        )}
        <button onClick={() => setShowResumen((s) => !s)} style={{
          padding: "10px 18px", borderRadius: 12, border: "none", cursor: "pointer",
          background: showResumen ? "linear-gradient(135deg,#312e81,#1e1b4b)" : "linear-gradient(135deg,#6366f1,#4338ca)",
          color: "#fff", fontWeight: 800, fontSize: 13.5, letterSpacing: 0.2,
          display: "flex", alignItems: "center", gap: 7,
          boxShadow: showResumen ? "0 4px 14px rgba(30,27,75,0.4)" : "0 4px 14px rgba(99,102,241,0.4)",
          transition: "box-shadow .15s, background .15s",
        }}>
          <span style={{ fontSize: 16 }}>{showResumen ? "📋" : "📊"}</span>
          {showResumen ? "Ver tabla" : "Ver resumen"}
        </button>
      </div>

      {/* Panel de resumen */}
      {showResumen && (
        <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <KpiCard icon="📋" label="Visitas obligatorias" value={totalesGlobales.total} bg="#eef2ff" color="#4338ca" />
            <KpiCard icon="✅" label="Reportes enviados" value={totalesGlobales.enviados} bg={BG.verde} color={C.verde} />
            <KpiCard icon="⬜" label="Sin enviar" value={totalesGlobales.sinEnviar} bg={BG.rojo} color={C.rojo} />
            <KpiCard icon="📊" label="% Cumplimiento" value={totalesGlobales.pct != null ? `${totalesGlobales.pct}%` : "—"} bg={BG.azul} color={C.azul} />
            <KpiCard icon="✨" label="Cobertura extra" value={filasExtra.length} bg="#f3e8ff" color="#7c3aed" />
          </div>
          <AtencionSinReportes filasSemana={filas} loading={loading} error={error} mostrarAsistenta={false} />

          {statsPorDia.length > 1 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                <h3 style={{ ...st.sh, margin: 0 }}>{vistaDia === "linea" ? "📈 Evolución del cumplimiento" : "📅 Cumplimiento por día"}</h3>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {filasExtra.length > 0 && <BotonSumarExtras activo={sumarExtras} onClick={() => setSumarExtras((v) => !v)} />}
                  <ToggleVista valor={vistaDia} onChange={setVistaDia} opciones={[{ k: "linea", label: "📈 Línea" }, { k: "barras", label: "📊 Barras" }]} />
                </div>
              </div>
              {sumarExtras && <NotaExtrasSumados />}
              {vistaDia === "linea" ? (
                <EvolucionCumplimiento dias={statsPorDiaGraf} />
              ) : statsPorDiaGraf.map((d) => (
                <BarraCumplimiento key={d.fecha}
                  label={new Date(d.fecha).toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "short" })}
                  enviados={d.enviados} total={d.total} pct={d.pct} extra={d.extra} extraJuntoNumero
                />
              ))}
            </div>
          )}

          {statsPorDia.length > 1 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                <h3 style={{ ...st.sh, margin: 0 }}>🗓️ Calendario de cumplimiento</h3>
                {filasExtra.length > 0 && <BotonSumarExtras activo={sumarExtras} onClick={() => setSumarExtras((v) => !v)} />}
              </div>
              {sumarExtras && <NotaExtrasSumados />}
              <CalendarioCumplimiento dias={statsPorDiaGraf} />
            </div>
          )}

          <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
            <h3 style={st.sh}>🚦 Cumplimiento por ruta</h3>
            {statsPorRuta.length === 0 ? (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>Sin datos suficientes.</div>
            ) : statsPorRuta.map((r) => (
              <BarraCumplimiento key={r.nombre} label={r.nombre} enviados={r.enviados} total={r.total} pct={r.pct} extra={r.extra} />
            ))}
          </div>

          {!esSupervisora && statsSupervisoras.length > 1 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
              <h3 style={st.sh}>👩‍💼 Cumplimiento por supervisora</h3>
              {statsSupervisoras.map((s) => {
                const pulsable = statsPorDia.length > 1;
                const abierta = pulsable && supAbierta === s.nombre;
                return (
                  <div key={s.nombre}>
                    <div onClick={pulsable ? () => setSupAbierta((x) => (x === s.nombre ? null : s.nombre)) : undefined} title={pulsable ? "Pulsa para ver su evolución" : undefined}
                      style={{ display: "flex", alignItems: "center", gap: 10, cursor: pulsable ? "pointer" : "default", padding: "4px 6px 0", borderRadius: 10, background: abierta ? "#eef2ff" : "transparent" }}>
                      {pulsable && <span style={{ color: "#6366f1", fontSize: 13, transition: "transform .2s", transform: abierta ? "rotate(90deg)" : "none" }}>▸</span>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <BarraCumplimiento label={s.nombre} enviados={s.enviados} total={s.total} pct={s.pct} extra={s.extra} />
                      </div>
                      <MiniTendencia serie={seriesSupervisoras.get(s.nombre) || []} media={statsPorDia} />
                    </div>
                    {abierta && <DetalleSupervisora dias={seriesSupervisoras.get(s.nombre) || []} media={statsPorDia} Barra={BarraCumplimiento} />}
                  </div>
                );
              })}
              {statsPorDia.length > 1 && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Pulsa en una supervisora para ver su evolución.</div>}
            </div>
          )}

          <RespetoHorarios filas={filas} agrupaciones={AGRUPACIONES_RESPETO} />

          <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
            <h3 style={{ ...st.sh, marginBottom: 8 }}>🌡️ Heatmap por pregunta <span style={{ fontWeight: 500, color: "#94a3b8", fontSize: 12 }}>({filasHeatmap.length} filas del período seleccionado)</span></h3>
            {!esSupervisora && opcionesSupervisora.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>👩‍💼 Supervisora:</span>
                <select value={filtroSupervisoraHeatmap} onChange={(e) => setFiltroSupervisoraHeatmap(e.target.value)}
                  style={{
                    padding: "8px 14px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13,
                    background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3",
                    cursor: "pointer", boxShadow: "0 2px 8px rgba(79,70,229,0.15)", outline: "none",
                  }}>
                  <option value="todas">Todas las supervisoras</option>
                  {opcionesSupervisora.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}
            {preguntas.length === 0 ? (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>No hay preguntas activas en las categorías seleccionadas.</div>
            ) : preguntas.map((p) => <HeatmapPregunta key={p.id} pregunta={p} filas={filasHeatmap} />)}
          </div>

          {preguntas.length > 0 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
              <h3 style={{ ...st.sh, marginBottom: 8 }}>📈 Evolución de las preguntas <span style={{ fontWeight: 500, color: "#94a3b8", fontSize: 12 }}>(% de respuestas en verde)</span></h3>
              <EvolucionPreguntas preguntas={preguntas} filas={filas} contar={contarPregunta} />
            </div>
          )}
        </div>
      )}

      {/* Tabla principal */}
      {!showResumen && (
      <div style={{ padding: "12px 14px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 50, color: "#64748b" }}>⏳ Cargando…</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: 30, color: C.rojo }}>{error}</div>
        ) : filasVisibles.length === 0 ? (
          <div style={{ textAlign: "center", padding: 50, color: "#94a3b8" }}>No hay pisos con visita esperada en este período.</div>
        ) : (
          <>
            <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 700, color: "#334155" }}>
              Mostrando {filasVisibles.length} fila{filasVisibles.length !== 1 ? "s" : ""}
            </div>
            {nSinReporte > 0 && (
              <div style={{ marginBottom: 10, fontSize: 12.5, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ ...st.badge, background: "#94a3b8" }}>⬜</span> {nSinReporte} pisos sin reporte enviado ese día
              </div>
            )}
            <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(15,23,42,0.05)" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    {multiDia ? (
                      <>
                        <th rowSpan={2} style={{ ...st.th, minWidth: 90, left: 0, zIndex: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span title="Filtrar por piso" style={{ display: "inline-flex", alignItems: "center" }}>
                              🏠<ColumnFilterPopover opciones={opcionesPiso} seleccionados={colFiltros.piso} onChange={(v) => actualizarColFiltro("piso", v)} />
                            </span>
                            {!esSupervisora && (
                              <span title="Filtrar por supervisora" style={{ display: "inline-flex", alignItems: "center" }}>
                                👩‍💼<ColumnFilterPopover opciones={opcionesSupervisora} seleccionados={colFiltros.supervisora} onChange={(v) => actualizarColFiltro("supervisora", v)} renderOption={renderOpcionSupervisora} />
                              </span>
                            )}
                            <span title="Filtrar por ruta" style={{ display: "inline-flex", alignItems: "center" }}>
                              🚦<ColumnFilterPopover opciones={opcionesRuta} seleccionados={colFiltros.ruta} onChange={(v) => actualizarColFiltro("ruta", v)} renderLabel={(v) => RUTA_LABEL[v] || String(v)} />
                            </span>
                          </div>
                        </th>
                        <th rowSpan={2} style={st.th}>Asistenta</th>
                      </>
                    ) : (
                      <>
                        <th rowSpan={2} style={{ ...st.th, minWidth: 140, left: 0, zIndex: 4 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span>Piso</span>
                            <ColumnFilterPopover opciones={opcionesPiso} seleccionados={colFiltros.piso} onChange={(v) => actualizarColFiltro("piso", v)} />
                          </div>
                        </th>
                        <th rowSpan={2} style={st.th}>Asistenta</th>
                        <th rowSpan={2} style={st.th}>Servicio</th>
                        <th rowSpan={2} style={st.th}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span>Ruta</span>
                            <ColumnFilterPopover opciones={opcionesRuta} seleccionados={colFiltros.ruta} onChange={(v) => actualizarColFiltro("ruta", v)} renderLabel={(v) => RUTA_LABEL[v] || String(v)} />
                          </div>
                        </th>
                        {!esSupervisora && (
                          <th rowSpan={2} style={st.th}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <span>Supervisora</span>
                              <ColumnFilterPopover opciones={opcionesSupervisora} seleccionados={colFiltros.supervisora} onChange={(v) => actualizarColFiltro("supervisora", v)} renderOption={renderOpcionSupervisora} />
                            </div>
                          </th>
                        )}
                      </>
                    )}
                    <th rowSpan={2} style={{ ...st.th, minWidth: 150, textAlign: "center", zIndex: 5 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
                        <span>🕒 Hora / Tiempo medio</span>
                        <RangoTiempoPopover filtro={colFiltros.tiempo} onChange={(v) => actualizarColFiltro("tiempo", v)} />
                      </div>
                    </th>
                    {preguntasAgrupadas.map((g) => (
                      <th key={g.categoria} colSpan={g.preguntas.length} style={{ ...st.thCategoria, background: g.color.bg, color: g.color.text, borderBottom: `2px solid ${g.color.border}` }}>
                        {g.categoria}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {preguntasOrdenadas.map((p) => {
                      const color = colorDePregunta.get(p.id);
                      return (
                        <th key={p.id} style={{ ...st.th, minWidth: 140, textAlign: "center", background: color?.bg, top: 33 }}>
                          <div style={{ fontSize: 20 }}>{p.emoji}</div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                            <span style={{ fontSize: 14, fontWeight: 900, color: "#1e293b", whiteSpace: "normal", textTransform: "none", letterSpacing: 0.1, marginTop: 2 }}>{p.subcategoria}</span>
                            <ColumnFilterPopover
                              opciones={opcionesPregunta(p)}
                              seleccionados={colFiltros.preguntas[p.id] || []}
                              onChange={(v) => actualizarFiltroPregunta(p.id, v)}
                              renderLabel={(val) => etiquetaValorPregunta(p, val)}
                            />
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {!multiDia ? (
                    carga.filasMostradas.map((f, i) => {
                      const bgFila = f.es_extra ? "#bbf7d0" : !f.enviado ? "#f8fafc" : (i % 2 ? "#fff" : "#fcfdfe");
                      return (
                      <tr key={`${f.id_piso}_${f.fecha}`} style={{ background: bgFila }}>
                        <td style={{ ...st.tdPiso, background: bgFila }}>
                          <div style={{ fontWeight: 800, color: "#1e293b" }}>{f.abreviatura}</div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>#{f.id_piso}</div>
                        </td>
                        <td style={st.td}>
                          <div>{f.asistenta || "—"}</div>
                          {(f.hora_entrada || f.hora_salida) && (
                            <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600, marginTop: 1 }}>
                              🕐 {f.hora_entrada || "?"}–{f.hora_salida || "?"}
                            </div>
                          )}
                        </td>
                        <td style={st.td}>
                          {f.servicio && (() => {
                            const sc = getServicioStyle(f.servicio);
                            return <span style={{ ...st.badgeServicio, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{f.servicio}</span>;
                          })()}
                        </td>
                        <td style={st.td}>
                          <span style={{ ...st.badgeRuta, background: RUTA_COLOR[f.ruta] || "#94a3b8" }}>{RUTA_LABEL[f.ruta] || "—"}</span>
                          {f.es_extra && <span style={{ ...st.badgeExtra, marginLeft: 6 }} title="Cobertura fuera del día habitual de la ruta">✨ Extra</span>}
                        </td>
                        {!esSupervisora && <td style={st.td}>{renderOpcionSupervisora(f.user_supervisora)}</td>}
                        <td style={{ ...st.td, textAlign: "center" }}>
                          <CeldaHoraTiempo f={f} />
                        </td>
                        {!f.enviado ? (
                          <td colSpan={preguntasOrdenadas.length} style={{ ...st.td, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>
                            ⬜ Sin reporte
                          </td>
                        ) : (
                          preguntasOrdenadas.map((p) => (
                            <td key={p.id} style={{ ...st.td, textAlign: "center" }}>
                              {f.respuestas_alumnos?.[String(p.id)] ? (
                                <CeldaResidentes pregunta={p} resumen={f.respuestas_alumnos[String(p.id)]} total={f.n_alumnos} />
                              ) : (
                                <CeldaRespuesta pregunta={p} valor={f.respuestas?.[String(p.id)]} />
                              )}
                            </td>
                          ))
                        )}
                      </tr>
                      );
                    })
                  ) : (
                    carga.gruposMostrados.map((g) => (
                      <React.Fragment key={g.piso.id_piso}>
                        <tr>
                          <td colSpan={3 + preguntasOrdenadas.length} style={st.grupoHeaderCell}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 20, fontWeight: 900, color: "#312e81", display: "flex", alignItems: "center", gap: 8 }}>
                                🏠 {g.piso.abreviatura}
                                <span style={st.chipId}>#{g.piso.id_piso}</span>
                              </span>
                              {g.piso.servicio && (() => {
                                const sc = getServicioStyle(g.piso.servicio);
                                return <span style={{ ...st.badgeServicio, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{g.piso.servicio}</span>;
                              })()}
                              <span style={{ ...st.badgeRuta, background: RUTA_COLOR[g.piso.ruta] || "#94a3b8" }}>{RUTA_LABEL[g.piso.ruta] || "—"}</span>
                              {!esSupervisora && <span style={st.chipSupervisora}>👩‍💼 {renderOpcionSupervisora(g.piso.user_supervisora)}</span>}
                              <span style={{ ...st.chipDias, marginLeft: "auto" }}>📅 {g.dias.length} día{g.dias.length !== 1 ? "s" : ""}</span>
                            </div>
                          </td>
                        </tr>
                        {g.dias.map((f) => {
                          const bgFila = f.es_extra ? "#bbf7d0" : !f.enviado ? "#f8fafc" : "#fff";
                          return (
                          <tr key={f.fecha} style={{ background: bgFila }}>
                            <td style={{ ...st.tdFecha, background: bgFila }}>
                              {new Date(f.fecha).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "2-digit" })}
                              {f.es_extra && <span style={{ ...st.badgeExtra, marginLeft: 6 }} title="Cobertura fuera del día habitual de la ruta">✨</span>}
                            </td>
                            <td style={st.td}>
                              <div>{f.asistenta || "—"}</div>
                              {(f.hora_entrada || f.hora_salida) && (
                                <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600, marginTop: 1 }}>
                                  🕐 {f.hora_entrada || "?"}–{f.hora_salida || "?"}
                                </div>
                              )}
                            </td>
                            <td style={{ ...st.td, textAlign: "center" }}>
                              <CeldaHoraTiempo f={f} />
                            </td>
                            {!f.enviado ? (
                              <td colSpan={preguntasOrdenadas.length} style={{ ...st.td, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>
                                ⬜ Sin reporte
                              </td>
                            ) : (
                              preguntasOrdenadas.map((p) => (
                                <td key={p.id} style={{ ...st.td, textAlign: "center" }}>
                                  {f.respuestas_alumnos?.[String(p.id)] ? (
                                <CeldaResidentes pregunta={p} resumen={f.respuestas_alumnos[String(p.id)]} total={f.n_alumnos} />
                              ) : (
                                <CeldaRespuesta pregunta={p} valor={f.respuestas?.[String(p.id)]} />
                              )}
                                </td>
                              ))
                            )}
                          </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <MasFilas {...carga} />
          </>
        )}
      </div>
      )}
    </div>
  );
};

const st = {
  sh: { margin: "0 0 10px", fontSize: 14, fontWeight: 800, color: "#334155" },
  th: { padding: "9px 12px", fontSize: 11.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1.5px solid #e2e8f0", whiteSpace: "nowrap", textAlign: "left", background: "#f8fafc", position: "sticky", top: 0, zIndex: 2 },
  thCategoria: { padding: "7px 12px", fontSize: 12.5, fontWeight: 800, textAlign: "center", whiteSpace: "nowrap", height: 33, boxSizing: "border-box", position: "sticky", top: 0, zIndex: 3 },
  td: { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", color: "#334155" },
  tdPiso: { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 1 },
  tdFecha: { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", color: "#475569", fontWeight: 700, fontSize: 12, textTransform: "capitalize", position: "sticky", left: 0, zIndex: 1 },
  grupoHeaderCell: { padding: "16px 18px", background: "linear-gradient(135deg,#eef2ff,#e0e7ff)", borderTop: "3px solid #818cf8", borderBottom: "1.5px solid #c7d2fe", color: "#312e81", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)" },
  chipId: { fontSize: 12, fontWeight: 700, color: "#4338ca", background: "#fff", borderRadius: 8, padding: "2px 9px" },
  chipSupervisora: { fontSize: 13, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", borderRadius: 20, padding: "5px 12px" },
  chipDias: { fontSize: 12.5, fontWeight: 800, color: "#4338ca", background: "#fff", border: "1.5px solid #c7d2fe", borderRadius: 20, padding: "5px 12px" },
  badge: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, minWidth: 26, height: 24, padding: "0 8px", borderRadius: 12, fontSize: 12, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" },
  badgeExtra: { display: "inline-flex", alignItems: "center", gap: 4, background: "#dcfce7", color: "#15803d", border: "1.5px solid #86efac", borderRadius: 10, padding: "3px 9px", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" },
  badgeBajaFiltro: { display: "inline-block", marginLeft: 6, padding: "1px 7px", fontSize: 10.5, fontWeight: 800, color: "#fff", backgroundColor: "#e74c3c", borderRadius: 8, verticalAlign: "middle" },
  badgeRuta: { display: "inline-block", padding: "3px 10px", borderRadius: 10, fontSize: 12, fontWeight: 800, letterSpacing: 0.2, whiteSpace: "nowrap", color: "#fff" },
  badgeServicio: { display: "inline-block", padding: "3px 10px", borderRadius: 10, fontSize: 12, fontWeight: 800, letterSpacing: 0.2, whiteSpace: "nowrap" },
};

export default VerReporteSupervisoras;
